import { bindUIFocus, type UIFocusTarget } from './UIButton';
import { UITheme } from './UITheme';
import * as Phaser from 'phaser';
import { EventBus } from '../../src/core/EventBus';
import { LocalizationManager } from '../managers/LocalizationManager';
import { createLogger } from '../utils/Logger';

/**
 * Интерфейс для описания варианта улучшения
 */
interface UpgradeChoice {
  id: string;
  type?: string;
  title: string;
  description: string;
  icon?: string;
  color?: number;
}

/**
 * Константы для стилизации UI
 */
const UI_STYLE = {
  backgroundColor: UITheme.background,
  backgroundAlpha: 0.94,
  textColor: UITheme.text,
  fontSize: '24px',
  fontFamily: UITheme.body,
  buttonColor: UITheme.panel,
  buttonHoverColor: UITheme.hover,
  buttonPadding: 10,
  containerPadding: 20
};

/**
 * Класс LevelUpScreen - экран выбора улучшений при повышении уровня
 * @extends Phaser.GameObjects.Container
 */
export class LevelUpScreen extends Phaser.GameObjects.Container {
  private releaseFocus?: () => void;
  private readonly focusTargets: UIFocusTarget[] = [];
  private focusedChoiceIndex = -1;
  private choices: UpgradeChoice[] = [];
  private choiceContainers: Phaser.GameObjects.Container[] = [];
  private background: Phaser.GameObjects.Graphics | null = null;
  private titleText: Phaser.GameObjects.Text | null = null;
  private readonly logger = createLogger('LevelUpScreen');
  private reducedFlashes = false;

  public setReducedFlashes(value: boolean): void {
    this.reducedFlashes = value;
    if (value) this.choiceContainers.forEach(container => { this.scene.tweens.killTweensOf(container); container.setAlpha(1); });
  }

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.setDepth(1000);
    this.setVisible(false);
  }

  showChoices(choices: UpgradeChoice[]): void {
    this.logger.info('showChoices:start', { choices: choices.length });
    this.clearChoices();
    this.choices = [...choices];
    this.createBackground();
    this.createTitle(); // Add title
    this.setVisible(true);
    this.resize();
    this.releaseFocus = bindUIFocus(this.scene, this.focusTargets);
    this.logger.debug('showChoices:complete');
  }

  hide(): void {
    this.releaseFocus?.(); this.releaseFocus = undefined;
    this.logger.info('hide:start');
    this.scene.tweens.killTweensOf(this);
    this.choiceContainers.forEach(container => this.scene.tweens.killTweensOf(container));
    this.setVisible(false);
    this.clearChoices();
    this.logger.debug('hide:complete');
  }

  public resize(): void {
    if (!this.visible) return;
    const width = this.scene.scale.width;
    const height = this.scene.scale.height;
    
    if (this.background) {
      this.background.clear();
      this.background.fillStyle(UI_STYLE.backgroundColor, UI_STYLE.backgroundAlpha);
      this.background.fillRect(0, 0, width, height);
    }
    
    const scaleFactor = 1;
    
    if (this.titleText) {
      const isCompact = this.isCompactLayout();
      const titleY = isCompact ? 35 : 65;
      this.titleText.setPosition(width / 2, titleY);
      this.titleText.setScale(scaleFactor);
    }

    this.choiceContainers.forEach(container => {
      this.scene.tweens.killTweensOf(container);
      container.destroy();
    });
    this.choiceContainers = [];
    // Сохраняем группу в стеке фокуса; resize не открывает новое окно.
    this.focusTargets.length = 0;
    this.createChoiceButtons();
    this.positionElements();
    this.focusTargets[this.focusedChoiceIndex]?.setFocused(true);
  }

  private clearChoices(): void {
    this.releaseFocus?.(); this.releaseFocus = undefined;
    this.focusedChoiceIndex = -1;
    this.focusTargets.length = 0;
    this.choiceContainers.forEach(container => {
      container.destroy();
    });
    this.choiceContainers = [];

    if (this.background) {
      this.background.destroy();
      this.background = null;
    }

    if (this.titleText) {
      this.titleText.destroy();
      this.titleText = null;
    }
  }

  private createBackground(): void {
    this.background = new Phaser.GameObjects.Graphics(this.scene);
    const width = this.scene.scale.width;
    const height = this.scene.scale.height;
    this.background.fillStyle(UI_STYLE.backgroundColor, UI_STYLE.backgroundAlpha);
    this.background.fillRect(0, 0, width, height);
    this.add(this.background);
  }

  private createTitle(): void {
    const width = this.scene.scale.width;
    const loc = LocalizationManager.getInstance();
    this.titleText = this.scene.add.text(width / 2, 100, loc.getText('screen.levelup.title'), {
      fontSize: '28px',
      color: '#ecc778',
      fontFamily: UITheme.heading
    }).setOrigin(0.5);
    this.add(this.titleText);
  }

  private createChoiceButtons(): void {
    this.choices.forEach((choice, _index) => {
      const container = this.scene.add.container(0, 0);
      container.setScrollFactor(0); // Ensure input hit testing works correctly in screen space

      // Card dimensions
      const compact = this.isCompactLayout();
      const cardWidth = compact ? Math.min(300, (this.scene.scale.width - 48) / Math.max(1, this.choices.length) - 12) : Math.min(520, this.scene.scale.width - 40);
      const cardHeight = compact ? 230 : 150; // Fixed height for consistency

      // Graphics for background (Normal)
      const bgNormal = this.scene.add.graphics();
      bgNormal.fillStyle(UI_STYLE.buttonColor, 1);
      bgNormal.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 10);
      bgNormal.lineStyle(2, 0x748d72, 1);
      bgNormal.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 10);

      // Graphics for background (Hover)
      const bgHover = this.scene.add.graphics();
      bgHover.fillStyle(UI_STYLE.buttonHoverColor, 1);
      bgHover.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 10);
      bgHover.lineStyle(2, UITheme.warning, 1);
      bgHover.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 10);
      bgHover.setVisible(false);

      // Interactive area (hit area based on card dimensions)
      const hitArea = new Phaser.Geom.Rectangle(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight);
      container.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

      const iconColor = choice.color ?? 0x666666;
      const iconBg = this.scene.add.rectangle(-cardWidth / 2 + 32, -cardHeight / 2 + 34, 44, 44, iconColor, 0.95);
      iconBg.setStrokeStyle(1, 0xffffff, 0.5);

      const iconLabel = this.scene.add.text(-cardWidth / 2 + 32, -cardHeight / 2 + 34, this.getShortLabel(choice), {
        fontSize: '12px',
        color: '#ffffff',
        fontFamily: UI_STYLE.fontFamily,
        fontStyle: 'bold'
      }).setOrigin(0.5);

      container.add([bgNormal, bgHover, iconBg, iconLabel]);
      if (choice.icon && this.scene.textures.exists(choice.icon)) {
        iconLabel.setVisible(false);
        const iconSprite = this.scene.add.sprite(-cardWidth / 2 + 32, -cardHeight / 2 + 34, choice.icon);
        iconSprite.setDisplaySize(34, 34);
        container.add(iconSprite);
      }

      const title = this.scene.add.text(-cardWidth / 2 + 62, -cardHeight / 2 + 16, choice.title, {
        fontSize: '18px', color: UITheme.text, fontFamily: UITheme.body,
        fontStyle: 'bold', wordWrap: { width: cardWidth - 76 }
      });
      const categoryY = Math.max(-cardHeight / 2 + 62, title.y + title.height + 6);
      const category = this.scene.add.text(-cardWidth / 2 + 16, categoryY, LocalizationManager.getInstance().getText(choice.type === 'consumable' ? 'ui.consumables' : choice.type?.startsWith('passive') ? 'ui.passives' : 'ui.weapons'), {fontFamily: UITheme.body, fontSize: '16px', color: '#ecc778'});
      const text = this.scene.add.text(-cardWidth / 2 + 16, categoryY + 24, choice.description, {
        fontSize: '16px', color: UITheme.muted, fontFamily: UITheme.body,
        wordWrap: { width: cardWidth - 32 }
      });
      container.add([title, category, text]);

      // Interactions
      const activate = () => {
        if(!this.visible || !this.choices.includes(choice))return;
        this.logger.info('choice:selected', { id: choice.id, title: choice.title });
        this.hide();
        EventBus.getInstance().publish({ type: 'upgrade:selected', data: { choice } });
      };
      container.on('pointerdown', (_p:unknown,_x:number,_y:number,e?:Phaser.Types.Input.EventData) => {e?.stopPropagation();activate();});
      this.focusTargets.push({setFocused: focused => {
        this.focusedChoiceIndex = focused ? _index : -1;
        bgNormal.setVisible(!focused);
        bgHover.setVisible(focused);
      },activate});

      container.on('pointerover', () => {
        bgNormal.setVisible(false);
        bgHover.setVisible(true);
        this.scene.input.setDefaultCursor('pointer');
        // Slight scale up for effect? keeping it simple for now to avoid positioning issues
        // container.setScale(1.05); // Avoiding scale to prevent layout shifts
      });

      container.on('pointerout', () => {
        bgNormal.setVisible(true);
        bgHover.setVisible(false);
        this.scene.input.setDefaultCursor('default');
        // container.setScale(1);
      });

      this.add(container);
      this.choiceContainers.push(container);
      container.setAlpha(this.reducedFlashes ? 1 : 0.55);
      if (this.reducedFlashes) return;
      this.scene.tweens.add({
        targets: container,
        alpha: 1,
        duration: 220,
        delay: _index * 70,
        ease: 'Sine.easeOut'
      });
    });
  }

  private getShortLabel(choice: UpgradeChoice): string {
    if (choice.type === 'consumable') return '+';
    return choice.title.split(' ').map(part => part.charAt(0)).join('').slice(0, 2).toUpperCase();
  }

  private isCompactLayout(): boolean {
    const { width, height } = this.scene.scale;
    const columnHeight = this.choices.length * 150 + Math.max(0, this.choices.length - 1) * 15;
    // Оставляем место заголовку и отступам при вертикальном размещении.
    return width > height && height < Math.max(500, columnHeight + 120);
  }

  private positionElements(): void {
    if (this.choiceContainers.length === 0) return;

    const width = this.scene.scale.width;
    const height = this.scene.scale.height;
    const scaleFactor = 1;
    const isCompact = this.isCompactLayout();

    if (isCompact) {
      this.logger.debug('positionElements:layout', { layout: 'horizontal', scaleFactor });
      const cardWidth = Math.min(300, (width - 48) / this.choiceContainers.length - 12);
      const spacing = 15;
      const totalWidth = this.choiceContainers.length * (cardWidth + spacing) * scaleFactor - spacing * scaleFactor;
      const startX = (width - totalWidth) / 2 + (cardWidth / 2) * scaleFactor;
      const centerY = height * 0.55;

      this.choiceContainers.forEach((container, index) => {
        container.setPosition(startX + index * (cardWidth + spacing) * scaleFactor, centerY);
        container.setScale(scaleFactor);
      });
    } else {
      this.logger.debug('positionElements:layout', { layout: 'vertical', scaleFactor });
      const cardHeight = 150;
      const spacing = 15;
      const totalHeight = this.choiceContainers.length * (cardHeight + spacing) * scaleFactor - spacing * scaleFactor;
      const startY = (height - totalHeight) / 2 + (cardHeight / 2) * scaleFactor + (this.titleText ? 30 * scaleFactor : 0);

      this.choiceContainers.forEach((container, index) => {
        container.setPosition(width / 2, startY + index * (cardHeight + spacing) * scaleFactor);
        container.setScale(scaleFactor);
      });
    }
  }
}
